import React from 'react';
import { createRoot } from 'react-dom/client';
import App from './App.jsx';
import { APP_VERSION } from './ui.jsx';
import './styles.css';
import { isKnownWebKitMediaNoise } from './media-utils.js';

// CORRECTIF v4.2.1 (iOS) : crypto.randomUUID n'existe que depuis iOS/Safari
// 15.4. Sur un appareil plus ancien, TOUT envoi de message et TOUTE création
// de groupe plantent avec « crypto.randomUUID is not a function » (tmpId,
// groupId, mid… en dépendent partout : App.jsx, ws.js). Polyfill conforme à
// la RFC 4122 v4, basé sur getRandomValues (dispo depuis iOS 11) — appliqué
// AVANT tout le reste, il couvre tous les appels du bundle.
if (typeof crypto !== 'undefined' && !crypto.randomUUID) {
  crypto.randomUUID = function randomUUID() {
    const b = crypto.getRandomValues(new Uint8Array(16));
    b[6] = (b[6] & 0x0f) | 0x40; // version 4
    b[8] = (b[8] & 0x3f) | 0x80; // variante RFC 4122
    const h = [...b].map((x) => x.toString(16).padStart(2, '0')).join('');
    return `${h.slice(0, 8)}-${h.slice(8, 12)}-${h.slice(12, 16)}-${h.slice(16, 20)}-${h.slice(20)}`;
  };
}

// v4.2 (diagnostic) : filet de sécurité visuel. Un écran blanc silencieux est
// impossible à diagnostiquer sans un poste de développement connecté (câble +
// inspecteur), ce qui n'est pas toujours disponible dans l'instant, surtout
// sur mobile. Ce filet affiche le message d'erreur exact à l'écran plutôt que
// de laisser la page vide — purement un outil de diagnostic local, aucune
// donnée n'est envoyée nulle part (pas de service de rapport de crash tiers).
function formatError(error, componentStack = '') {
  const name = error?.name || 'Error';
  const message = error?.message || String(error || 'Erreur inconnue');
  const stack = error?.stack ? String(error.stack) : '';
  return [
    `Nom : ${name}`,
    `Message : ${message}`,
    stack ? `Pile JavaScript :\n${stack}` : '',
    componentStack ? `Pile des composants React :${componentStack}` : '',
  ].filter(Boolean).join('\n\n');
}

class ErrorBoundary extends React.Component {
  constructor(props) { super(props); this.state = { error: null, componentStack: '' }; }
  static getDerivedStateFromError(error) { return { error }; }
  componentDidCatch(error, info) {
    console.error('Erreur applicative :', error, info);
    this.setState({ componentStack: info?.componentStack || '' });
  }
  render() {
    if (this.state.error) {
      return (
        <div style={{
          padding: 20, fontFamily: 'monospace', fontSize: 13, color: '#c00',
          background: '#fff', height: '100%', overflow: 'auto', whiteSpace: 'pre-wrap',
        }}>
          <h2 style={{ color: '#c00' }}>Erreur applicative (diagnostic) — build {APP_VERSION}</h2>
          <p>Une erreur a empêché l'affichage normal. Copiez l'intégralité du diagnostic ci-dessous.</p>
          <pre>{formatError(this.state.error, this.state.componentStack)}</pre>
          <button onClick={() => { this.setState({ error: null, componentStack: '' }); location.reload(); }}
            style={{ marginTop: 12, padding: '8px 16px' }}>Recharger</button>
        </div>
      );
    }
    return this.props.children;
  }
}

// Erreurs hors du cycle de rendu React (event handlers, promesses non
// rattrapées) : affichées de la même façon plutôt que silencieusement perdues
// dans la console (inaccessible sans inspecteur connecté).
function GlobalErrorOverlay() {
  const [err, setErr] = React.useState(null);
  React.useEffect(() => {
    const onError = (e) => {
      const value = e.error || e.message || String(e);
      if (isKnownWebKitMediaNoise(value)) { e.preventDefault?.(); console.warn('Erreur média WebKit ignorée (EmptyRanges).'); return; }
      setErr(value);
    };
    const onRejection = (e) => {
      const value = e.reason || String(e);
      if (isKnownWebKitMediaNoise(value)) { e.preventDefault?.(); console.warn('Rejet média WebKit ignoré (EmptyRanges).'); return; }
      setErr(value);
    };
    window.addEventListener('error', onError);
    window.addEventListener('unhandledrejection', onRejection);
    return () => {
      window.removeEventListener('error', onError);
      window.removeEventListener('unhandledrejection', onRejection);
    };
  }, []);
  if (!err) return null;
  return (
    <div style={{
      position: 'fixed', bottom: 0, left: 0, right: 0, maxHeight: '40%', overflow: 'auto',
      background: '#fff3f3', borderTop: '2px solid #c00', color: '#c00',
      fontFamily: 'monospace', fontSize: 12, padding: 12, zIndex: 9999, whiteSpace: 'pre-wrap',
    }}>
      <strong>Erreur (diagnostic) :</strong>
      <button style={{ float: 'right' }} onClick={() => setErr(null)}>✕</button>
      <div>{formatError(err)}</div>
    </div>
  );
}

createRoot(document.getElementById('root')).render(
  <ErrorBoundary>
    <GlobalErrorOverlay />
    <App />
  </ErrorBoundary>
);

// PWA (v2.0) : service worker de cache statique uniquement (voir public/sw.js).
if ('serviceWorker' in navigator) {
  window.addEventListener('load', () => navigator.serviceWorker.register('/sw.js').catch(() => {}));
}
